import { ContentContext, CountryContext, LocaleContext } from 'Components/hooks/context';
import React, { useContext, useState } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import Cautions from './Cautions';
import D3Locale from '@reuters-graphics/d3-locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Options from './Options';
import SmartText from '@reuters-graphics/react-smart-text';
import StripPlot from './StripPlot';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

import inRange from 'lodash/inRange';
import last from 'lodash/last';
import max from 'lodash/maxBy';
import mustache from 'mustache';
import pickBy from 'lodash/pickBy';

const atlas = new AtlasMetadataClient();

const perPopFactor = 1000000;

const formatData = (dataObj, getter = d => d, perPop, percentChange) =>

  Object.keys(dataObj)
    .filter(isoAlpha2 => perPop ? atlas.getCountry(isoAlpha2).dataProfile.population !== null : true)
    .filter(isoAlpha2 => perPop ? atlas.getCountry(isoAlpha2).dataProfile.population.d > 0 : true)
    .filter(isoAlpha2 => percentChange ? (dataObj[isoAlpha2][3] > 0 && dataObj[isoAlpha2][2] > 0) : true)
    .map(isoAlpha2 => ({
      key: isoAlpha2,
      value: perPop ?
        parseFloat(dataObj[isoAlpha2]) / (atlas.getCountry(isoAlpha2).dataProfile.population.d / perPopFactor) :
        getter(dataObj[isoAlpha2]),
    }));

const getRegionData = (dataObj, country, getter, perPop = false, percentChange = false) => {
  const countries = atlas
    .getCountriesByRegion(country.region.slug)
    .map(country => country.isoAlpha2);
  const data = pickBy(dataObj, (v, k) => countries.includes(k));

  return formatData(data, getter, perPop, percentChange);
};

const getGlobalData = (dataObj, getter, perPop = false, percentChange = false) => formatData(dataObj, getter, perPop, percentChange);

const _getCountryListInBin = (data, value, param) => {
  const bins = param === 'cases' ?
    // Checks if n is between start and up to, but not including end
    [
      [0, 25], // less than 25
      [25, 100], // 25-100
      [100, 250], // 100-250
      [250, max(data, 'value').value + 1], // 250 or more
    ] :
    [
      [0, 1], // less than 1
      [1, 10], // 1 to 10
      [10, max(data, 'value').value + 1], // 10 or more
    ];

  const activeBin = bins.filter(bin => inRange(value, bin[0], bin[1]))[0];

  // get bin position for note
  const activeBinPos = bins.findIndex(bin => bin === activeBin);
  let notePos = '';
  if (activeBinPos === 0) {
    notePos = 'start';
  } else if (activeBinPos === bins.length - 1) {
    notePos = 'end';
  } else {
    notePos = 'middle';
  }

  const countriesInRange = data.filter(country => inRange(country.value, activeBin[0], activeBin[1])).map(country => country.key);

  return { countriesInRange: countriesInRange, bin: activeBin, notePos: notePos };
};

const getChartData = (metric, globalStatistics, country, locale) => {
  const annotation = {
    key: country.isoAlpha2,
    text: country.translations[locale],
  };

  const countriesWithSimilarCaseCounts = _getCountryListInBin(getGlobalData(globalStatistics.latestWeeklyAvgs.cases, d => last(d)), getGlobalData(globalStatistics.latestWeeklyAvgs.cases, d => last(d)).find(d => d.key === country.isoAlpha2).value, 'cases');

  const countriesWithSimilarDeathCounts = _getCountryListInBin(getGlobalData(globalStatistics.latestWeeklyAvgs.deaths, d => last(d)), getGlobalData(globalStatistics.latestWeeklyAvgs.deaths, d => last(d)).find(d => d.key === country.isoAlpha2).value, 'deaths');
  switch (metric) {
    case 'total':
      return [{
        cases: {
          global: getGlobalData(globalStatistics.latestTotals.cases),
          regional: getRegionData(globalStatistics.latestTotals.cases, country),
        },
        deaths: {
          global: getGlobalData(globalStatistics.latestTotals.deaths),
          regional: getRegionData(globalStatistics.latestTotals.deaths, country),
        },
      }, annotation];
    case 'total_per_pop':
      return [{
        cases: {
          global: getGlobalData(globalStatistics.latestTotals.cases, d => d, true),
          regional: getRegionData(globalStatistics.latestTotals.cases, country, d => d, true),
        },
        deaths: {
          global: getGlobalData(globalStatistics.latestTotals.deaths, d => d, true),
          regional: getRegionData(globalStatistics.latestTotals.deaths, country, d => d, true),
        },
      }, annotation];
    case 'average':
      return [{
        cases: {
          global: getGlobalData(globalStatistics.latestWeeklyAvgs.cases, d => last(d)),
          regional: getRegionData(globalStatistics.latestWeeklyAvgs.cases, country, d => last(d)),
        },
        deaths: {
          global: getGlobalData(globalStatistics.latestWeeklyAvgs.deaths, d => last(d)),
          regional: getRegionData(globalStatistics.latestWeeklyAvgs.deaths, country, d => last(d)),
        },
      }, annotation];
    case 'change':
      var data = {
        cases: {},
        deaths: {},
      };
      var regionData = {
        cases: {},
        deaths: {},
      };
      var showFilter = true;

      // no filtering by bins for 'oceania' and 'northern-america'
      if (country.region.slug === 'oceania' || country.region.slug === 'northern-america') {
        regionData.cases = globalStatistics.latestWeeklyAvgs.cases;
        regionData.deaths = globalStatistics.latestWeeklyAvgs.deaths;
        showFilter = false;
      } else {
        countriesWithSimilarCaseCounts.countriesInRange.forEach(c => {
          regionData.cases[c] = globalStatistics.latestWeeklyAvgs.cases[c];
        });
        countriesWithSimilarDeathCounts.countriesInRange.forEach(c => {
          regionData.deaths[c] = globalStatistics.latestWeeklyAvgs.deaths[c];
        });
      }
      countriesWithSimilarCaseCounts.countriesInRange.forEach(c => {
        data.cases[c] = globalStatistics.latestWeeklyAvgs.cases[c];
      });
      countriesWithSimilarDeathCounts.countriesInRange.forEach(c => {
        data.deaths[c] = globalStatistics.latestWeeklyAvgs.deaths[c];
      });

      return [{
        cases: {
          global: getGlobalData(data.cases, d => ((d[3] - d[2]) / d[2]) * 100, false, true),
          regional: getRegionData(regionData.cases, country, d => ((d[3] - d[2]) / d[2]) * 100, false, true),
        },
        deaths: {
          global: getGlobalData(data.deaths, d => ((d[3] - d[2]) / d[2]) * 100, false, true),
          regional: getRegionData(regionData.deaths, country, d => ((d[3] - d[2]) / d[2]) * 100, false, true),
        },
      }, annotation, countriesWithSimilarCaseCounts.bin, countriesWithSimilarCaseCounts.notePos, countriesWithSimilarDeathCounts.bin, countriesWithSimilarDeathCounts.notePos, showFilter];
  }
};

const CountryCompare = (props) => {
  const { globalStatistics } = props;
  const locale = useContext(LocaleContext);
  const countrySlug = useContext(CountryContext);
  const content = useContext(ContentContext).template.compare;

  const country = atlas.getCountry(countrySlug);
  const [metric, setMetric] = useState('total');
  const d3Locale = new D3Locale(locale);

  const _getOptions = (country, content) => {
    let optionList = [{
      metric: 'total',
      buttonText: content.total.button,
    }, {
      metric: 'total_per_pop',
      buttonText: content.totalPerPop.button,
    }, {
      metric: 'average',
      buttonText: content.averageDaily.button,
    }, {
      metric: 'change',
      buttonText: content.averageChange.button,
    }];
    // show change option only if country has data
    if (!(globalStatistics.latestWeeklyAvgs.cases[country][2] && globalStatistics.latestWeeklyAvgs.cases[country][3] && globalStatistics.latestWeeklyAvgs.deaths[country][2] && globalStatistics.latestWeeklyAvgs.deaths[country][3])) {
      optionList = optionList.filter(option => option.metric !== 'change');
      // reset activeMetric state if new country doesn't have the metric
    }
    // show perPop only if population data != 0
    if (!(atlas.getCountry(country).dataProfile.population)) {
      optionList = optionList.filter(option => option.metric !== 'total_per_pop');
    }
    return optionList;
  };

  const [chartData, annotation, binCases, notePosCases, binDeaths, notePosDeaths, showFilter] = getChartData(metric, globalStatistics, country, locale);

  const buttonOptions = _getOptions(country.isoAlpha2, content);

  return (
    <div className={classnames(component)}>
      <div className='section-spacer before' />
      <div className='section-spacer-internal before' />
      <div>
        <h4>
          <SmartText
            source={content.title}
            context={{ country: country.translations[locale] }}
          />
        </h4>
        <SmartText
          source={content.intro}
          multiline
        />
        <div className='section-spacer-internal' />
        <div className='control-box'>
          <Options
            activeMetric={metric}
            setMetric={
              (metric === 'change' && !buttonOptions.find(d => d.metric === 'change')) || (metric === 'total_per_pop' && !buttonOptions.find(d => d.metric === 'total_per_pop')) ? setMetric('total') : setMetric
            }
            options={buttonOptions}
          />
          <Cautions metric={metric} content={content} />
        </div>
        <div className='section-spacer-internal' />
        <div className='pack'>
          <div className='clearfix'>
            <StripPlot
              id='aszd'
              annotation={{ ...annotation, text: '' }}
              data={chartData.cases.regional}
              locale={locale}
              activeMetric={metric}
              content={{
                title: mustache.render(content.regionCasesTitle, {
                  region: country.region.translations[locale],
                }),
                axisMin: metric === 'change' ?
                  content.decreasingLabel : content.minLabel,
                axisMax: metric === 'change' ?
                  content.increasingLabel : content.maxLabel,
              }}
              showSplitAxis={metric === 'change'}
              icon={metric === 'change' && showFilter ? <FontAwesomeIcon icon={faFilter} /> : ''}
            />
            <StripPlot
              id='asd'
              annotation={annotation}
              data={chartData.cases.global}
              locale={locale}
              activeMetric={metric}
              content={{
                title: content.globalCasesTitle,
              }}
              showSplitAxis={metric === 'change'}
              icon={metric === 'change' ? <FontAwesomeIcon icon={faFilter} /> : ''}
            />

          </div>
        </div>
        {metric === 'change' ?
          <div className='note'>
            <p>
              <FontAwesomeIcon icon={faFilter} />
              {notePosCases === 'middle' ?
                mustache.render(' ' + content.averageChangeCases.note, {
                  min: d3Locale.format('.3~s')(binCases[0]),
                  max: d3Locale.format('.3~s')(binCases[1]),
                }) : notePosCases === 'start' ?
                  mustache.render(' ' + content.averageChangeCases.notemin, {
                    max: d3Locale.format('.3~s')(binCases[1]),
                  }) :
                  mustache.render(' ' + content.averageChangeCases.notemax, {
                    min: d3Locale.format('.3~s')(binCases[0]),
                  })}

            </p>
          </div> : ''}
        <div className='pack'>
          <div className='clearfix'>
            <StripPlot
              id='aszdzz'
              annotation={{ ...annotation, text: '' }}
              data={chartData.deaths.regional}
              locale={locale}
              activeMetric={metric}
              content={{
                title: mustache.render(content.regionDeathsTitle, {
                  region: country.region.translations[locale],
                }),
                axisMin: metric === 'change' ?
                  content.decreasingLabel : content.minLabel,
                axisMax: metric === 'change' ?
                  content.increasingLabel : content.maxLabel,
              }}
              showSplitAxis={metric === 'change'}
              icon={metric === 'change' && showFilter ? <FontAwesomeIcon icon={faFilter} /> : ''}
            />
            <StripPlot
              id='aszzd'
              annotation={{ ...annotation, text: '' }}
              data={chartData.deaths.global}
              locale={locale}
              activeMetric={metric}
              content={{
                title: content.globalDeathsTitle,
              }}
              showSplitAxis={metric === 'change'}
              icon={metric === 'change' ? <FontAwesomeIcon icon={faFilter} /> : ''}
            />

          </div>
        </div>
        {metric === 'change' ?
          <div className='note'>
            <p>
              <FontAwesomeIcon icon={faFilter} />
              {notePosDeaths === 'middle' ?
                mustache.render(' ' + content.averageChangeDeaths.note, {
                  min: d3Locale.format('.3~s')(binDeaths[0]),
                  max: d3Locale.format('.3~s')(binDeaths[1]),
                }) : notePosDeaths === 'start' ?
                  mustache.render(' ' + content.averageChangeDeaths.notemin, {
                    max: d3Locale.format('.3~s')(binDeaths[1]),
                  }) :
                  mustache.render(' ' + content.averageChangeDeaths.notemax, {
                    min: d3Locale.format('.3~s')(binDeaths[0]),
                  })}

            </p>
          </div> : ''}

        {metric === 'total_per_pop' ?
          <div className='note'>
            <p>
              {mustache.render(' * ' + content.totalPerPop.note, {
                num: (perPopFactor / 1000000) === 1 ? '' : perPopFactor / 1000000,
              })}
            </p>
          </div> : ''}

        <div className='section-spacer' />
      </div>
    </div>
  );
};

export default CountryCompare;
